// src/components/configuration/sections/ZoomIntegrationSettings.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

const ZoomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0">
    <rect width="24" height="24" rx="4" fill="#2D8CFF"/>
    <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h7A1.5 1.5 0 0 1 14 8.5v7A1.5 1.5 0 0 1 12.5 17h-7A1.5 1.5 0 0 1 4 15.5v-7zm10 2.1 4.5-2.8a.5.5 0 0 1 .75.43v6.54a.5.5 0 0 1-.75.43L14 12.4v-1.8z" fill="white"/>
  </svg>
);

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';

// Zoom hook
import { useZoom } from '../../../hooks/useZoom';

/**
 * Zoom Integration Settings Component
 *
 * Handles OAuth for Zoom meeting integration
 *
 * IMPORTANT: This component handles OAuth redirects from Zoom
 * - On mount, checks for ?success=true&section=integrations query params
 * - Flow: Disconnected → OAuth → Connected
 */
const ZoomIntegrationSettings = () => {
  const {
    // Connection status
    zoomConnection,
    zoomConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Account details
    accountEmail,

    // Disconnection
    disconnecting,

    // Operations
    loadZoomConnectionStatus,
    startZoomOAuth,
    disconnectZoom
  } = useZoom();

  // Local UI state
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState('loading'); // loading, disconnected, connected
  const [oauthError, setOauthError] = useState(null);

  // ================================
  // OAUTH REDIRECT HANDLING
  // ================================

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorMessage = urlParams.get('message');
    const section = urlParams.get('section');

    if (section === 'integrations') {
      if (success === 'true') {
        console.log('✅ ZoomIntegrationSettings: OAuth success detected');
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => loadZoomConnectionStatus(), 1000);
      } else if (error) {
        console.error('❌ ZoomIntegrationSettings: OAuth error detected:', error, errorMessage);
        setOauthError(errorMessage || error);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [loadZoomConnectionStatus]);

  // ================================
  // DETERMINE CURRENT STEP
  // ================================

  useEffect(() => {
    if (zoomConnectionLoading) {
      setCurrentStep('loading');
      return;
    }
    if (connected) {
      setCurrentStep('connected');
    } else {
      setCurrentStep('disconnected');
    }
  }, [connected, zoomConnectionLoading]);

  // ================================
  // OAUTH URL GENERATED - REDIRECT
  // ================================

  useEffect(() => {
    if (oauthUrl) {
      console.log('🚀 ZoomIntegrationSettings: OAuth URL generated, redirecting to Zoom...');
      window.location.href = oauthUrl;
    }
  }, [oauthUrl]);

  // ================================
  // HANDLERS
  // ================================

  const handleStartOAuth = () => {
    setOauthError(null);
    startZoomOAuth();
  };

  const handleDisconnect = () => {
    disconnectZoom();
    setShowDisconnectConfirm(false);
    setCurrentStep('disconnected');
  };

  // ================================
  // RENDER HELPERS
  // ================================

  const renderLoading = () => (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Spinner className="size-4" />
      <span>Loading...</span>
    </div>
  );

  const renderDisconnectedState = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Connect your Zoom account to automatically create meetings when customers book video conference services.
      </p>
      {oauthError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{oauthError}</AlertDescription>
        </Alert>
      )}
      <Button onClick={handleStartOAuth} disabled={oauthInitiating}>
        {oauthInitiating ? (
          <>
            <Spinner className="size-4 mr-2" />
            Connecting...
          </>
        ) : (
          <>
            <ZoomIcon />
            Connect with Zoom
          </>
        )}
      </Button>
    </div>
  );

  const renderConnectedState = () => (
    <div className="space-y-3">
      {/* Token Expired Warning */}
      {tokenExpired && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your Zoom connection has expired. Please reconnect to continue using Zoom meetings.
          </AlertDescription>
        </Alert>
      )}

      {/* Connected row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
          <span className="text-sm text-foreground">{accountEmail || 'Zoom Account'}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDisconnectConfirm(true)}
          disabled={disconnecting}
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
        </Button>
      </div>

      {/* Inline disconnect confirm */}
      {showDisconnectConfirm && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">Disconnect this account?</span>
          <button onClick={handleDisconnect} className="text-destructive hover:underline">Yes, disconnect</button>
          <button onClick={() => setShowDisconnectConfirm(false)} className="text-muted-foreground hover:text-foreground">Cancel</button>
        </div>
      )}
    </div>
  );

  // ================================
  // MAIN RENDER
  // ================================

  return (
    <div className="max-w-3xl">
      {currentStep === 'loading' && renderLoading()}
      {currentStep === 'disconnected' && renderDisconnectedState()}
      {currentStep === 'connected' && renderConnectedState()}
    </div>
  );
};

export default ZoomIntegrationSettings;
